import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useEffect } from "react";
import { Segmented, Button } from "@perfectserve/library";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import InteractionDetailModal from "../InteractionDetailModal";
import CallFlowAnalysisModal from "../CallFlowAnalysisModal";
import { usePlatformMetrics } from "../../hooks/usePlatformMetrics";
import { getInteractionColor, normalizeInteractionFinalAction, } from "../../types/platformMetrics";
import { createTimeOfDayData, createDayOfWeekData } from "../../utils/chartDataUtils";
import { analyzeFlowPatterns } from "../../utils/interactionUtils";
import { sanitizeNodeName } from "../../utils/mermaidUtils";
import { CallOutcomesBreakdown } from "./CallOutcomesBreakdown";
import { CallOutcomesListing } from "./CallOutcomesListing";
import { CallOutcomesDiagram } from "./CallOutcomesDiagram";
import "@perfectserve/css-styles-react";
export const CallOutcomesView = ({ onClose, allInteractions, className = "", contextTitle = "", }) => {
    const { fetchPlatformVolumeMetrics } = usePlatformMetrics();
    const [activeTab, setActiveTab] = useState("breakdown");
    const [selectedOutcome, setSelectedOutcome] = useState("");
    const [hoveredSegment, setHoveredSegment] = useState(null);
    const [selectedInteraction, setSelectedInteraction] = useState(null);
    const [callFlowInteraction, setCallFlowInteraction] = useState(null);
    const [platformData, setPlatformData] = useState(null);
    const [platformLoading, setPlatformLoading] = useState(false);
    const [platformDataLoaded, setPlatformDataLoaded] = useState(false);
    const [showPlatformData, setShowPlatformData] = useState(false);
    const finalActionsData = useMemo(() => {
        const outcomeCount = {};
        const outcomeDurations = {};
        allInteractions.forEach((interaction) => {
            const finalAction = normalizeInteractionFinalAction(interaction.InteractionFinalAction);
            const normalizedAction = finalAction === "Agent Text" ? "AgentText" : finalAction;
            outcomeCount[normalizedAction] = (outcomeCount[normalizedAction] || 0) + 1;
            const duration = interaction.CallDurationSeconds || 0;
            if (!outcomeDurations[normalizedAction]) {
                outcomeDurations[normalizedAction] = [];
            }
            outcomeDurations[normalizedAction].push(duration);
        });
        const calculateMedian = (durations) => {
            if (durations.length === 0)
                return 0;
            const sorted = [...durations].sort((a, b) => a - b);
            const mid = Math.floor(sorted.length / 2);
            if (sorted.length % 2 === 0) {
                return Math.round((sorted[mid - 1] + sorted[mid]) / 2);
            }
            else {
                return sorted[mid];
            }
        };
        const finalActionsArray = Object.entries(outcomeCount)
            .map(([outcome, count]) => ({
            name: outcome,
            count: count,
            percentage: allInteractions.length > 0 ? Math.round((count / allInteractions.length) * 100) : 0,
            isSecure: outcome === "AgentText" || outcome === "VoiceMessage",
            color: getInteractionColor(outcome),
            medianDuration: calculateMedian(outcomeDurations[outcome] || []),
        }))
            .sort((a, b) => {
            return b.count - a.count;
        });
        return finalActionsArray;
    }, [allInteractions]);
    const totalCalls = useMemo(() => {
        return finalActionsData.reduce((sum, item) => sum + item.count, 0);
    }, [finalActionsData]);
    const availableOutcomes = useMemo(() => {
        const outcomes = Array.from(new Set(allInteractions.map((i) => normalizeInteractionFinalAction(i.InteractionFinalAction)).filter(Boolean))).sort();
        return outcomes;
    }, [allInteractions]);
    useEffect(() => {
        if (availableOutcomes.length > 0 && !selectedOutcome) {
            const defaultOutcome = availableOutcomes.includes("HangUp") ? "HangUp" : availableOutcomes[0] || "";
            setSelectedOutcome(defaultOutcome);
        }
    }, [availableOutcomes, selectedOutcome]);
    const outcomeAnalysis = useMemo(() => {
        if (!selectedOutcome || !availableOutcomes.includes(selectedOutcome)) {
            return {
                totalOutcomes: 0,
                repeatCallers: [],
                durationChartData: [],
                timeChartData: [],
                dayOfWeekChartData: [],
            };
        }
        const filteredInteractions = allInteractions.filter((interaction) => normalizeInteractionFinalAction(interaction.InteractionFinalAction) === selectedOutcome);
        const allCallCounts = {};
        allInteractions.forEach((interaction) => {
            const phoneNumber = interaction.FromCallbackNumber || "Unknown";
            allCallCounts[phoneNumber] = (allCallCounts[phoneNumber] || 0) + 1;
        });
        const repeatCallers = Object.entries(allCallCounts)
            .filter(([phoneNumber, count]) => {
            if (count < 5)
                return false;
            const hasAnyInteraction = allInteractions
                .filter((i) => i.FromCallbackNumber === phoneNumber)
                .some((i) => i.ToFilterOriginator || i.ToFilterPriority || i.ToFilterReason);
            return !hasAnyInteraction;
        })
            .filter(([phoneNumber]) => {
            return filteredInteractions.some((i) => i.FromCallbackNumber === phoneNumber);
        })
            .map(([phoneNumber, count]) => ({ phoneNumber, count }))
            .sort((a, b) => b.count - a.count);
        const durationBuckets = {};
        for (let i = 0; i < 300; i += 10) {
            const bucket = `${i}-${i + 9}`;
            durationBuckets[bucket] = {
                noInteraction: 0,
                persistentCalls: 0,
                withInteraction: 0,
            };
        }
        durationBuckets[">300"] = {
            noInteraction: 0,
            persistentCalls: 0,
            withInteraction: 0,
        };
        filteredInteractions.forEach((interaction) => {
            const duration = interaction.CallDurationSeconds || 0;
            if (isNaN(duration) || duration < 0) {
                return;
            }
            let bucket;
            if (duration >= 300) {
                bucket = ">300";
            }
            else {
                bucket = `${Math.floor(duration / 10) * 10}-${Math.floor(duration / 10) * 10 + 9}`;
            }
            if (!durationBuckets[bucket]) {
                console.warn(`Invalid duration bucket: ${bucket} for duration: ${duration}`);
                return;
            }
            const phoneNumber = interaction.FromCallbackNumber || "Unknown";
            const hasUserInput = interaction.ToFilterOriginator || interaction.ToFilterPriority || interaction.ToFilterReason;
            const isPersistentCaller = (allCallCounts[phoneNumber] || 0) >= 5;
            const bucketData = durationBuckets[bucket];
            if (hasUserInput) {
                bucketData.withInteraction++;
            }
            else if (isPersistentCaller) {
                bucketData.persistentCalls++;
            }
            else {
                bucketData.noInteraction++;
            }
        });
        const durationChartData = Object.entries(durationBuckets)
            .map(([range, counts]) => {
            const safeCounts = counts || { noInteraction: 0, persistentCalls: 0, withInteraction: 0 };
            return {
                range,
                duration: range === ">300" ? 999 : parseInt(range.split("-")[0] || "0"),
                noInteraction: safeCounts.noInteraction || 0,
                persistentCalls: safeCounts.persistentCalls || 0,
                withInteraction: safeCounts.withInteraction || 0,
                total: (safeCounts.noInteraction || 0) + (safeCounts.persistentCalls || 0) + (safeCounts.withInteraction || 0),
            };
        })
            .sort((a, b) => a.duration - b.duration);
        const timeChartData = createTimeOfDayData(filteredInteractions);
        const dayOfWeekChartData = createDayOfWeekData(filteredInteractions);
        return {
            totalOutcomes: filteredInteractions.length,
            repeatCallers,
            durationChartData,
            timeChartData,
            dayOfWeekChartData,
        };
    }, [allInteractions, selectedOutcome, availableOutcomes]);
    const flowPatterns = useMemo(() => {
        return analyzeFlowPatterns(allInteractions);
    }, [allInteractions]);
    const mermaidDiagramData = useMemo(() => {
        if (flowPatterns.length === 0)
            return null;
        const allNodes = new Set();
        const connections = [];
        const allScheduleNames = new Set();
        const topPatterns = flowPatterns.slice(0, 8);
        topPatterns.forEach((pattern) => {
            var _a, _b, _c, _d;
            const steps = pattern.pattern.split(" → ").filter((step) => step.trim());
            steps.forEach((step) => {
                const sanitizedStep = sanitizeNodeName(step.trim());
                if (sanitizedStep) {
                    allNodes.add(sanitizedStep);
                }
            });
            Object.keys(pattern.scheduleNames).forEach((schedule) => {
                const sanitizedSchedule = sanitizeNodeName(schedule);
                if (sanitizedSchedule) {
                    allScheduleNames.add(sanitizedSchedule);
                }
            });
            for (let i = 0; i < steps.length - 1; i++) {
                const fromStep = sanitizeNodeName(((_a = steps[i]) === null || _a === void 0 ? void 0 : _a.trim()) || "");
                const toStep = sanitizeNodeName(((_b = steps[i + 1]) === null || _b === void 0 ? void 0 : _b.trim()) || "");
                if (fromStep && toStep && fromStep !== toStep) {
                    const existingConnection = connections.find((conn) => conn.from === fromStep && conn.to === toStep);
                    if (existingConnection) {
                        existingConnection.count += pattern.count;
                    }
                    else {
                        connections.push({
                            from: fromStep,
                            to: toStep,
                            count: pattern.count,
                        });
                    }
                }
            }
            if (Object.keys(pattern.scheduleNames).length > 0) {
                const topSchedule = Object.entries(pattern.scheduleNames).sort(([, a], [, b]) => b - a)[0];
                if (topSchedule) {
                    const [scheduleName, scheduleCount] = topSchedule;
                    const sanitizedScheduleName = sanitizeNodeName(scheduleName);
                    if (sanitizedScheduleName && scheduleCount >= 3) {
                        allNodes.add(sanitizedScheduleName);
                        const greaterThanFirstStep = 2;
                        if (steps.length >= greaterThanFirstStep) {
                            const lastStep = sanitizeNodeName(((_c = steps[steps.length - 1]) === null || _c === void 0 ? void 0 : _c.trim()) || "");
                            const secondLastStep = sanitizeNodeName(((_d = steps[steps.length - 2]) === null || _d === void 0 ? void 0 : _d.trim()) || "");
                            if (secondLastStep &&
                                lastStep &&
                                secondLastStep !== sanitizedScheduleName &&
                                sanitizedScheduleName !== lastStep) {
                                connections.push({
                                    from: secondLastStep,
                                    to: sanitizedScheduleName,
                                    count: scheduleCount,
                                });
                                connections.push({
                                    from: sanitizedScheduleName,
                                    to: lastStep,
                                    count: scheduleCount,
                                });
                            }
                        }
                    }
                }
            }
        });
        const uniqueConnections = new Map();
        connections.forEach((conn) => {
            if (conn.from && conn.to && conn.from !== conn.to) {
                const key = `${conn.from}->${conn.to}`;
                const existing = uniqueConnections.get(key);
                if (existing) {
                    existing.count += conn.count;
                }
                else {
                    uniqueConnections.set(key, { ...conn });
                }
            }
        });
        const connectionsArray = Array.from(uniqueConnections.values());
        const filteredConnections = new Map();
        connectionsArray.forEach((conn) => {
            const reverseKey = `${conn.to}->${conn.from}`;
            const forwardKey = `${conn.from}->${conn.to}`;
            if (!filteredConnections.has(reverseKey)) {
                filteredConnections.set(forwardKey, conn);
            }
            else {
                const reverse = filteredConnections.get(reverseKey);
                if (conn.count > reverse.count) {
                    filteredConnections.delete(reverseKey);
                    filteredConnections.set(forwardKey, conn);
                }
            }
        });
        const limitComplexityCount = 20;
        const sortedConnections = Array.from(filteredConnections.values())
            .filter((conn) => conn.count > 0)
            .sort((a, b) => b.count - a.count)
            .slice(0, limitComplexityCount);
        const connectedNodes = new Set();
        sortedConnections.forEach((conn) => {
            connectedNodes.add(conn.from);
            connectedNodes.add(conn.to);
        });
        const orphanedNodes = Array.from(allNodes).filter((node) => !connectedNodes.has(node));
        let mermaidCode = "flowchart TD\n";
        const nodeIds = {};
        let nodeCounter = 1;
        Array.from(connectedNodes).forEach((node) => {
            const nodeId = `N${nodeCounter++}`;
            nodeIds[node] = nodeId;
            let displayName = node;
            const displayNameMaxLenth = 17;
            if (displayName.length > 20) {
                displayName = displayName.substring(0, displayNameMaxLenth) + "...";
            }
            displayName = displayName.replace(/"/g, "'").replace(/\n/g, " ");
            const isScheduleNode = allScheduleNames.has(node);
            if (isScheduleNode) {
                mermaidCode += `    ${nodeId}("${displayName}")\n`;
                mermaidCode += `    style ${nodeId} fill:#e8f4fd,stroke:#0066cc,stroke-width:2px\n`;
            }
            else {
                mermaidCode += `    ${nodeId}["${displayName}"]\n`;
                if (displayName.includes("Menu") || displayName.includes("Specialty")) {
                    mermaidCode += `    style ${nodeId} fill:#f0f9ff,stroke:#0369a1,stroke-width:2px\n`;
                }
                else if (displayName.includes("Transfer") || displayName.includes("Agent")) {
                    mermaidCode += `    style ${nodeId} fill:#f0fdf4,stroke:#16a34a,stroke-width:2px\n`;
                }
                else if (displayName.includes("HangUp") || displayName.includes("Cancel")) {
                    mermaidCode += `    style ${nodeId} fill:#fef2f2,stroke:#dc2626,stroke-width:2px\n`;
                }
            }
        });
        sortedConnections.forEach((conn) => {
            const fromId = nodeIds[conn.from];
            const toId = nodeIds[conn.to];
            if (fromId && toId) {
                const maxConnectionCount = 999;
                const countLabel = conn.count > maxConnectionCount ? `${Math.round(conn.count / 100) / 10}k` : conn.count.toString();
                mermaidCode += `    ${fromId} -->|${countLabel}| ${toId}\n`;
            }
        });
        if (orphanedNodes.length > 0 && orphanedNodes.length <= 5) {
            orphanedNodes.forEach((node) => {
                const nodeId = `E${nodeCounter++}`;
                let displayName = node;
                if (displayName.length > 20) {
                    displayName = displayName.substring(0, 17) + "...";
                }
                displayName = displayName.replace(/"/g, "'").replace(/\n/g, " ");
                mermaidCode += `    ${nodeId}["${displayName}"]\n`;
                mermaidCode += `    style ${nodeId} fill:#fff3cd,stroke:#856404,stroke-dasharray: 5 5\n`;
            });
        }
        return {
            mermaidCode,
            totalSteps: allNodes.size,
            totalTransitions: connections.length,
            totalInteractions: topPatterns.reduce((sum, p) => sum + p.count, 0),
            flowPatterns: topPatterns,
        };
    }, [flowPatterns]);
    const loadPlatformData = async () => {
        if (platformDataLoaded || platformLoading)
            return;
        setPlatformLoading(true);
        try {
            const data = await fetchPlatformVolumeMetrics("7d");
            setPlatformData(data);
            setPlatformDataLoaded(true);
            setShowPlatformData(true);
        }
        catch (error) {
            console.error("Failed to load platform data:", error);
            setPlatformDataLoaded(true);
        }
        finally {
            setPlatformLoading(false);
        }
    };
    useEffect(() => {
        return () => {
            setPlatformData(null);
            setPlatformDataLoaded(false);
            setPlatformLoading(false);
            setShowPlatformData(false);
        };
    }, []);
    const platformFinalActionsData = useMemo(() => {
        if (!platformData)
            return [];
        const totals = {};
        let totalInteractions = 0;
        platformData.weeklyMetrics.forEach((week) => {
            platformData.interactionTypes.forEach((type) => {
                const value = week[type] || 0;
                totals[type] = (totals[type] || 0) + value;
                totalInteractions += value;
            });
        });
        return Object.entries(totals)
            .filter(([_, count]) => count > 0)
            .map(([name, count]) => ({
            name,
            count,
            percentage: totalInteractions > 0 ? Math.round((count / totalInteractions) * 100) : 0,
            color: getInteractionColor(name),
            isSecure: name === "AgentText" || name === "VoiceMessage",
        }))
            .sort((a, b) => b.count - a.count);
    }, [platformData]);
    return (_jsxs("div", { className: `call-outcomes-container interactions-container ${className}`, children: [_jsx("style", { children: `
          .checkbox-no-focus input[type="checkbox"]:focus,
          .checkbox-no-focus input[type="checkbox"]:focus-visible,
          .checkbox-no-focus *:focus,
          .checkbox-no-focus *:focus-visible {
            outline: none !important;
            box-shadow: none !important;
          }
        ` }), _jsx("div", { className: "call-outcomes-header interactions-header", children: _jsxs("div", { className: "interactions-controls d-flex justify-content-between align-items-center w-100", children: [_jsx(Button, { onClick: onClose, icon: faArrowLeft, iconPosition: "left", children: "Back to Dashboard" }), _jsx("div", { className: "call-outcomes-title bold text-center flex-1", children: contextTitle || "All Selected Accounts" }), _jsx("div", { style: { width: "140px" } }), " "] }) }), _jsxs("div", { className: "bg-white border border-gray-300 rounded-8 shadow-sm mx-20 mb-20", style: { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }, children: [_jsx("div", { className: "call-outcomes-tabs d-flex justify-content-center p-20 pb-0", children: _jsx(Segmented, { name: "callOutcomesMainTabs", value: activeTab, onChange: (value) => setActiveTab(value), options: [
                                { value: "breakdown", label: "Call Outcomes" },
                                { value: "listing", label: "Call Listing" },
                                { value: "diagram", label: "Flow Diagram" },
                            ], size: "medium" }) }), _jsx("div", { id: "call-outcomes-content", className: "call-outcomes-content p-20", children: _jsx("div", { className: "call-outcomes-main", children: allInteractions.length === 0 ? (_jsxs("div", { className: "text-center p-50 text-muted fs-16", children: [_jsx("p", { children: "No interactions found for the selected filters." }), _jsx("p", { className: "fs-14 mt-10", children: "Try adjusting your date range or filter criteria." })] })) : activeTab === "breakdown" ? (_jsx(CallOutcomesBreakdown, { finalActionsData: finalActionsData, totalCalls: totalCalls, selectedOutcome: selectedOutcome, hoveredSegment: hoveredSegment, outcomeAnalysis: outcomeAnalysis, showPlatformData: showPlatformData, platformLoading: platformLoading, platformFinalActionsData: platformFinalActionsData, onOutcomeSelect: setSelectedOutcome, onSegmentHover: setHoveredSegment, onLoadPlatformData: loadPlatformData, onHidePlatformData: () => setShowPlatformData(false) })) : activeTab === "listing" ? (_jsx(CallOutcomesListing, { allInteractions: allInteractions, onInteractionSelect: setSelectedInteraction, onCallFlowSelect: setCallFlowInteraction })) : (_jsx(CallOutcomesDiagram, { mermaidDiagramData: mermaidDiagramData, flowPatterns: flowPatterns })) }) })] }), selectedInteraction && (_jsx(InteractionDetailModal, { open: !!selectedInteraction, onClose: () => setSelectedInteraction(null), interaction: selectedInteraction })), callFlowInteraction && (_jsx(CallFlowAnalysisModal, { open: !!callFlowInteraction, onClose: () => setCallFlowInteraction(null), interaction: callFlowInteraction }))] }));
};
export default CallOutcomesView;
//# sourceMappingURL=CallOutcomesView.js.map